<template>
	<div class="relative">
		<div class="absolute top-0 left-0 spinner z-40" v-if="store.isLoading"></div>

		<div v-show="store.hasNoResults">
			<p v-if="!singleTaxMode">No upcoming events, please try again.</p>
			<p v-if="singleTaxMode">No upcoming events for this category.</p>
		</div>

		<div v-for="year in store.groupedItems" :key="year.year" class="grid gap-16 mb-16">
			<!-- <h2 class="font-header uppercase text-md text-red-dark mb-6">{{ year.year }}</h2> -->

			<div
				v-for="month in year.months"
				:key="month.value"
				:id="`month-${month.value}`"
				data-month-section
				:data-month-value="month.value"
				class="scroll-mt-40"
			>
				<h3 class="heading-xl mb-8" v-show="!currentMonthOnly">
					{{ month.label }}
				</h3>
				<div class="grid gap-x-6 gap-y-12 items-start md:grid-cols-2 xl:grid-cols-3" :class="{ 'xl:grid-cols-4!': !showMonthNav }">
					<Event v-for="item in month.items" :key="item.ID" :item="item" :times="showTimes" />
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount, onMounted, nextTick } from "vue";
import { storeToRefs } from "pinia";
import { vIntersectionObserver } from "@vueuse/components";
import Event from "@facet/components/tiles/Event.vue";
import { useFeedStore } from "@facet/utils/store.js";

const store = useFeedStore();

const { currentMonth, groupedItems, singleTaxMode, showMonthNav, currentMonthOnly, showTimes } = storeToRefs(store);

const props = defineProps({
	items: {
		type: Array,
		required: true,
	},
	columns: {
		type: Number,
		default: 3,
	},
});

const localItems = ref([...props.items]);

watch(
	() => props.items,
	(newItems) => {
		localItems.value = [...newItems];
	}
);

const sections = ref([]);
const OFFSET = 256;
let rafId = 0;

function collectSections() {
	sections.value = Array.from(document.querySelectorAll("[data-month-section]"));
}

function computeActive() {
	if (!sections.value.length) return;

	const vh = window.innerHeight || document.documentElement.clientHeight;
	const scrollY = window.scrollY || window.pageYOffset;
	const scrollBottom = document.documentElement.scrollHeight - (scrollY + vh);

	// reference line: 256 normally, but clamps towards bottom
	const targetLine = scrollBottom < OFFSET ? vh - scrollBottom : OFFSET;

	let nearest = null;
	let minDist = Infinity;

	for (const el of sections.value) {
		const top = el.getBoundingClientRect().top;
		const dist = Math.abs(top - targetLine);
		if (dist < minDist) {
			minDist = dist;
			nearest = el;
		}
	}

	if (nearest) {
		const value = nearest.getAttribute("data-month-value") || nearest.id.replace("month-", "");
		if (value !== currentMonth.value) {
			currentMonth.value = value;
		}
	}
}

function onScroll() {
	if (rafId) return;
	rafId = requestAnimationFrame(() => {
		rafId = 0;
		computeActive();
	});
}

onMounted(async () => {
	await nextTick();
	collectSections();
	computeActive(); // set initial
	window.addEventListener("scroll", onScroll, { passive: true });
	window.addEventListener("resize", onScroll, { passive: true });
});

onBeforeUnmount(() => {
	window.removeEventListener("scroll", onScroll);
	window.removeEventListener("resize", onScroll);
	if (rafId) cancelAnimationFrame(rafId);
});

// re-collect when groupedItems change (e.g. filters)
watch(groupedItems, async () => {
	await nextTick();
	collectSections();
	computeActive();
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.33s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
